import { Fragment, useMemo } from 'react';
import { MutatorCallback, MutatorOptions } from 'swr';

import { ForeignDataTreeView } from '@/Pages/Graph/src/components/debug/common/foreign-data-tree';
import {
  foreignDataToTree,
  parseForeignData,
} from '@/Pages/Graph/src/data/foreign-data';
import { useAlignEvaluatorInfo } from '@/Pages/Playground/PlaygroundHome/hooks/useAlignEvaluator';
import Attachments from '@/Pages/Run/components/Attachments';
import { getAttachments } from '@/Pages/Run/utils/multimodalRunUtils';
import { CodeDataReadOnly } from '@/components/Code/Code';
import { PrettyJSONEditor } from '@/components/PrettyJSONEditor/PrettyJSONEditor';
import { useFeedbackConfigs } from '@/hooks/useSwr';
import {
  ComparisonViewColumn,
  DatasetDataType,
  DatasetSchema,
  ExampleSchemaWithRunsAndOptionalFields,
  ExperimentViewOverridesSchema,
  SessionSchema,
} from '@/types/schema';
import { getNestedValue } from '@/utils/get-nested-value';
import { isMessageLike } from '@/utils/messages';
import { cn } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { DatasetSessionCompareCellActions } from '../../DatasetSessionCompareCellActions';
import { DatasetSessionCompareInputs } from '../../DatasetSessionCompareInputs';
import { DatasetSessionCompareOutputs } from '../../DatasetSessionCompareOutputs';
import {
  DEFAULT_COLUMN_WIDTHS,
  DatasetSessionCompareSettings,
  OUTPUT_START_COLUMN_IDX,
  TextDisplayMode,
} from '../../constants';
import { TExamplePages } from '../../types';
import {
  DEFAULT_METRIC_NAMES,
  extractPrefixedKey,
  getColumnType,
} from '../../utils/columnTypeUtils';
import { isDatasetExample } from '../../utils/isDatasetExample';
import {
  type ColumnRenderFunctions,
  type ExperimentViewMetric,
  renderOverriddenColumns,
} from '../../utils/renderOverriddenColumns';
import { useGetExperimentDiffs } from '../../utils/useGetExperimentDiffs';
import { DatasetExampleButton } from '../DatasetExampleButton';
import { DatasetSessionCompareRunCell } from '../DatasetSessionCompareRunCell';
import { FeedbackCell } from '../SingleExperimentColumns/FeedbackColumns/FeedbackColumns';
import { MetricsColumn } from '../SingleExperimentColumns/MetricsColumns/MetricsColumns';
import { useSidePanes } from '../hooks/useSidePanes';
import { AttachmentsCell } from './AttachmentsCell';
import { InputsCell } from './InputsCell';
import { MetadataCell } from './MetadataCell';
import { MoreActionsButton } from './MoreActionsButton';
import { OutputsCell } from './OutputsCell';

export interface TableRowProps {
  example: ExampleSchemaWithRunsAndOptionalFields;
  setExampleInputs?: (inputs: Record<string, string>) => void;
  setExampleOutputs?: (outputs: Record<string, string>) => void;
  displaySettingsState: DatasetSessionCompareSettings;
  sidePanesControls: ReturnType<typeof useSidePanes>;
  columnWidthStorage: number[];
  dataset?: DatasetSchema;
  columnsSorted: ComparisonViewColumn[];
  experimentInProgress?: boolean;
  evaluatorFeedbackKeys?: string[];
  expectedFeedbackKeys?: string[];
  sessionIds: string[];
  columnIds: string[];
  columnOverrides?: ExperimentViewOverridesSchema['column_overrides'];
  isPairwiseUIEnabled: boolean;
  isRegressionTrackingUIEnabled: boolean;
  selectedBaselineSessionId?: string | null;
  regressionsFilterFeedbackKey?: string | null;
  mutateInfiniteExamples: (
    updateFn?: MutatorCallback<TExamplePages>,
    options?: MutatorOptions
  ) => void;
  comparativeExperiment: string | null;
  datasetShareToken?: string;
  aggregateFeedbackKeys: string[];
  dataType?: DatasetDataType;
  isEditable: boolean;
  heuristicCompactViewDisplayedKey?: string;
  onDelete?: () => void;
  isDeleteLoading?: boolean;
  customExtraTableCell?: (backgroundStyles: string) => React.ReactNode;
  sessionsData?: SessionSchema[];
  highlighted?: boolean;
}

export function TableRow({
  example,
  setExampleInputs,
  setExampleOutputs,
  displaySettingsState,
  sidePanesControls,
  columnWidthStorage,
  dataset,
  columnsSorted,
  experimentInProgress,
  evaluatorFeedbackKeys,
  expectedFeedbackKeys,
  sessionIds,
  columnIds,
  columnOverrides,
  isPairwiseUIEnabled,
  isRegressionTrackingUIEnabled,
  selectedBaselineSessionId,
  regressionsFilterFeedbackKey,
  mutateInfiniteExamples,
  comparativeExperiment,
  datasetShareToken,
  aggregateFeedbackKeys,
  dataType,
  isEditable,
  heuristicCompactViewDisplayedKey,
  onDelete,
  isDeleteLoading,
  customExtraTableCell,
  sessionsData,
  highlighted,
}: TableRowProps) {
  const {
    isReferenceInputHidden,
    isReferenceOutputHidden,
    isAttachmentsHidden,
    hideFeedback,
    hideMetrics,
    language,
    textDisplayMode,
    hiddenFeedbackColumns,
    hiddenMetricsColumns,
  } = displaySettingsState;

  const { getRunDiffs, storedDiffs } = useGetExperimentDiffs(language);

  const {
    reset,
    openDetailsOrRepetitionsPane,
    setDetailSingleExampleId,
    toggleRunDetail,
    openTraceId,
    rowDetailExampleId,
    detailSingleExampleId,
    toggleRowExampleDetail,
  } = sidePanesControls;

  const [feedbackChipsExpanded, setFeedbackChipsExpanded] =
    useLocalStorageState('ls:comparisonViewFeedbackChipsExpanded', false);

  const isDetailExample = useMemo(
    () =>
      rowDetailExampleId === example?.id ||
      detailSingleExampleId === example?.id ||
      openTraceId ===
        example?.runs.find((r) => r.trace_id === openTraceId)?.trace_id,
    [rowDetailExampleId, detailSingleExampleId, openTraceId, example]
  );

  let backgroundStyles = '';
  if (isDetailExample || highlighted) {
    backgroundStyles = 'active transition-colors duration-200';
  }

  const attachments = getAttachments(example.attachment_urls);

  const hoverButton = example.dataset_id ? (
    <DatasetSessionCompareCellActions
      example={example}
      onOpenDetailSidebar={toggleRowExampleDetail}
      className="pointer-events-none z-50 opacity-0 focus-within:pointer-events-auto focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100"
    />
  ) : undefined;

  const showFeedbackAndMetricsColumns =
    (sessionIds.length === 1 && columnIds.length === 0) ||
    (sessionIds.length === 0 && columnIds.length === 1);

  const isTextTruncated = textDisplayMode === TextDisplayMode.COMPACT;

  // Get filtered runs once for all feedback columns
  const filteredRuns = useMemo(
    () => example.runs.filter((run) => run.session_id === sessionIds[0]),
    [example.runs, sessionIds]
  );

  // Fetch all feedback configs at once
  const feedbackConfigs = useFeedbackConfigs(
    { skip: aggregateFeedbackKeys.length === 0 },
    { key: aggregateFeedbackKeys, read_after_write: true }
  );

  const { isAlignEvaluatorV2 } = useAlignEvaluatorInfo();

  // Render input columns (inputs, inputs.x)
  const renderInputsColumn = (columnName: string, columnIndex?: number) => {
    const isSpecificKey = columnName.startsWith('inputs.');
    const inputKey = isSpecificKey ? extractPrefixedKey(columnName) : undefined;

    // For specific keys, show only the field value, not as an object
    const inputsToShow =
      isSpecificKey && inputKey
        ? getNestedValue(example.inputs, inputKey)
        : example.inputs;

    return (
      <InputsCell
        key={`inputs-${inputKey || 'all'}-${example.id}`}
        isReferenceInputHidden={isReferenceInputHidden}
        isTextTruncated={isTextTruncated}
        backgroundStyles={backgroundStyles}
        columnWidthStorage={columnWidthStorage}
        hideFeedback={hideFeedback}
        isDetailExample={isDetailExample}
        columnIndex={columnIndex}
        inputContent={
          isEditable && setExampleInputs && !isSpecificKey ? (
            <PrettyJSONEditor
              readOnly={!isEditable}
              value={example.inputs}
              setValue={setExampleInputs}
              keyPlaceholder="input"
              valuePlaceholder="Type input value here..."
            />
          ) : (
            <DatasetSessionCompareInputs
              inputs={inputsToShow}
              heuristicCompactViewDisplayedKey={
                heuristicCompactViewDisplayedKey
              }
              dataType={dataset?.data_type ?? 'kv'}
              language={language}
              truncate={isTextTruncated}
              lineClamp={1}
            />
          )
        }
        hoverButton={hoverButton}
        exampleButton={
          isDatasetExample(example) &&
          !isSpecificKey &&
          !isAlignEvaluatorV2 && (
            <DatasetExampleButton
              example={example}
              isTextTruncated={isTextTruncated}
              onClick={() => {
                reset();
                setDetailSingleExampleId(example.id ?? null);
              }}
            />
          )
        }
      />
    );
  };

  // Render attachment columns
  const renderAttachmentsColumn = (columnIndex?: number) => {
    return (
      <AttachmentsCell
        key={`attachments-${example.id}`}
        columnIndex={columnIndex}
        isAttachmentsHidden={isAttachmentsHidden}
        isTextTruncated={isTextTruncated}
        backgroundStyles={backgroundStyles}
        columnWidthStorage={columnWidthStorage}
        hasAttachments={attachments.length > 0}
        hoverButton={hoverButton}
        attachmentContent={
          <Attachments
            attachments={attachments}
            className="p-0"
            gridClassName={cn(
              'flex gap-2 p-0',
              isTextTruncated && 'flex-nowrap'
            )}
            hideTitle
            size="small"
          />
        }
      />
    );
  };

  // Render reference output columns (reference_outputs, reference_outputs.x)
  const renderReferenceOutputsColumn = (
    columnName: string,
    columnIndex?: number
  ) => {
    const isSpecificKey = columnName.startsWith('reference_outputs.');

    // For specific keys, show only the field value, not as an object
    const outputsToShow = isSpecificKey
      ? getNestedValue(
          example.outputs,
          columnName.split('.').slice(1).join('.')
        )
      : example.outputs;

    return (
      <OutputsCell
        key={`reference-outputs-${columnName || 'all'}-${example.id}`}
        columnIndex={columnIndex}
        isReferenceOutputHidden={isReferenceOutputHidden}
        isTextTruncated={isTextTruncated}
        backgroundStyles={backgroundStyles}
        columnWidthStorage={columnWidthStorage}
        hoverButton={hoverButton}
        outputContent={
          isEditable && setExampleOutputs && !isSpecificKey ? (
            <PrettyJSONEditor
              readOnly={!isEditable}
              value={example.outputs}
              setValue={setExampleOutputs}
              keyPlaceholder="output"
              valuePlaceholder="Type output value here..."
            />
          ) : (
            <DatasetSessionCompareOutputs
              outputs={outputsToShow}
              dataType={dataset?.data_type ?? 'kv'}
              language={language}
              truncate={isTextTruncated}
              inputs={example.inputs}
              lineClamp={1}
              displayAsCode={true}
              diffs={
                textDisplayMode === TextDisplayMode.DIFF &&
                language !== 'pretty'
                  ? storedDiffs[example.id]?.exampleOutput
                  : undefined
              }
            />
          )
        }
      />
    );
  };

  // Render experiment output columns (outputs, outputs.x)
  const renderOutputsColumn = (columnName: string, columnIndex: number) => {
    const sessionId = sessionIds[0];
    const matchingColumn = columnsSorted.find(
      (col) => col['sessionId'] === sessionId || col['columnId'] === sessionId
    );

    if (!matchingColumn) return null;

    const width = columnWidthStorage[columnIndex] ?? DEFAULT_COLUMN_WIDTHS;
    const isSpecificKey = columnName.startsWith('outputs.');
    const outputKey = isSpecificKey
      ? columnName.split('.').slice(1).join('.')
      : undefined;

    const columnOverride = columnOverrides?.find(
      (col) => col.column === columnName
    );
    return (
      <DatasetSessionCompareRunCell
        key={`outputs-${outputKey || sessionId}-${example.id}`}
        columnOverride={columnOverride}
        hiddenFeedbackKeys={displaySettingsState.hiddenFeedbackColumns}
        hiddenMetrics={displaySettingsState.hiddenMetricsColumns}
        experimentInProgress={experimentInProgress}
        expectedFeedbackKeys={
          example.runs.every((r) => r.error) ? [] : evaluatorFeedbackKeys
        }
        expanded={feedbackChipsExpanded}
        setExpanded={setFeedbackChipsExpanded}
        openDetailsOrRepetitionsPane={openDetailsOrRepetitionsPane}
        example={example}
        column={matchingColumn}
        sessionIds={sessionIds}
        isPairwiseUIEnabled={isPairwiseUIEnabled}
        isRegressionTrackingUIEnabled={isRegressionTrackingUIEnabled}
        selectedBaselineSessionId={selectedBaselineSessionId}
        regressionsFilterFeedbackKey={regressionsFilterFeedbackKey}
        isTextTruncated={isTextTruncated}
        getRunDiffs={getRunDiffs}
        diffs={
          textDisplayMode === TextDisplayMode.DIFF && language !== 'pretty'
            ? storedDiffs[example.id]?.runs
            : undefined
        }
        language={language}
        onOpenDetailSidebar={toggleRowExampleDetail}
        onOpenTraceDetailSidebar={toggleRunDetail}
        backgroundStyles={backgroundStyles}
        hideFeedback={hideFeedback}
        hideMetrics={hideMetrics}
        columnIndex={columnIndex}
        dataType={
          dataType ??
          (example.runs.every((r) => isMessageLike(r.outputs?.output))
            ? 'chat'
            : 'kv')
        }
        mutateInfiniteExamples={mutateInfiniteExamples}
        comparativeExperiment={comparativeExperiment ?? undefined}
        datasetShareToken={datasetShareToken}
        style={{
          width,
          maxWidth: width,
          minWidth: width,
        }}
        onViewEvaluatorRun={toggleRunDetail}
        sessions={sessionsData}
      />
    );
  };

  // Render feedback columns
  const renderFeedbackColumn = (
    columnName: string,
    columnIndex: number,
    columnOverride?: ExperimentViewOverridesSchema['column_overrides'][number]
  ) => {
    const feedbackKey = extractPrefixedKey(columnName);
    if (hiddenFeedbackColumns.includes(feedbackKey)) return null;

    const feedbackConfig = feedbackConfigs.data?.find(
      (fc) => fc.feedback_key === feedbackKey
    );

    return (
      <FeedbackCell
        key={`feedback-${feedbackKey}-${example.id}`}
        feedbackKey={feedbackKey}
        runs={filteredRuns}
        mutateInfiniteExamples={mutateInfiniteExamples}
        datasetShareToken={datasetShareToken}
        expectedFeedbackKeys={expectedFeedbackKeys}
        columnIndex={columnIndex}
        columnWidthStorage={columnWidthStorage}
        isTruncated={isTextTruncated}
        setOpenRunId={toggleRunDetail}
        experimentInProgress={!!experimentInProgress}
        feedbackConfig={feedbackConfig}
        backgroundStyles={backgroundStyles}
        sessions={sessionsData}
        columnOverride={columnOverride}
      />
    );
  };

  // Render metrics columns
  const renderMetricsColumn = (columnName: string, columnIndex: number) => {
    const metricType = extractPrefixedKey(columnName);
    if (hiddenMetricsColumns.includes(metricType)) return null;

    return (
      <MetricsColumn
        key={`metrics-${String(metricType)}-${example.id}`}
        metricType={metricType}
        sessionId={sessionIds[0]}
        example={example}
        columnWidthStorage={columnWidthStorage}
        columnIndex={columnIndex}
        isTruncated={isTextTruncated}
        experimentInProgress={!!experimentInProgress}
        backgroundStyles={backgroundStyles}
      />
    );
  };

  // Render metadata columns
  const renderMetadataColumn = (columnName: string, columnIndex: number) => {
    const metadataKey = extractPrefixedKey(columnName);
    const sessionId = sessionIds[0];

    // Find the run for this session
    const run = example.runs.find((r) => r.session_id === sessionId);

    // Get the specific metadata value if a key is specified, otherwise show all metadata
    const metadataToShow =
      run && metadataKey && metadataKey !== 'metadata'
        ? getNestedValue(run.extra?.metadata, metadataKey)
        : run?.extra?.metadata;

    // Determine what content to show
    let metadataContent: React.ReactNode;
    if (!run || !metadataToShow) {
      // Show empty content when no run or no metadata
      metadataContent = null;
    } else if (language === 'pretty') {
      metadataContent = (
        <ForeignDataTreeView
          data={foreignDataToTree({
            data: parseForeignData(metadataToShow),
          })}
        />
      );
    } else {
      // Show specific metadata value
      metadataContent = (
        <CodeDataReadOnly
          className="transition-all"
          language={language}
          value={metadataToShow}
          omitMaxHeight
        />
      );
    }

    return (
      <MetadataCell
        key={`metadata-${metadataKey ?? 'all'}-${example.id}`}
        isTextTruncated={isTextTruncated}
        backgroundStyles={backgroundStyles}
        columnWidthStorage={columnWidthStorage}
        columnIndex={columnIndex}
        metadataContent={metadataContent}
      />
    );
  };

  // Render computed/specific columns (inputs.field, outputs.field, etc.)
  const renderComputedColumn = (columnName: string, columnIndex: number) => {
    const columnType = getColumnType(columnName);

    switch (columnType) {
      case 'inputs':
        return renderInputsColumn(columnName, columnIndex);
      case 'reference_outputs':
        return renderReferenceOutputsColumn(columnName, columnIndex);
      case 'outputs':
        return renderOutputsColumn(columnName, columnIndex);
      case 'metadata':
        return renderMetadataColumn(columnName, columnIndex);
      default:
        return null;
    }
  };

  // Render columns based on columnOverride if available
  let overriddenColumns: React.ReactNode[] | null = null;
  if (columnOverrides) {
    const renderFunctions: ColumnRenderFunctions = {
      renderInputsColumn,
      renderAttachmentsColumn,
      renderReferenceOutputsColumn,
      renderOutputsColumn: (columnIndex: number) =>
        renderOutputsColumn('outputs', columnIndex),
      renderFeedbackColumn: (feedbackKey: string, columnIndex: number) =>
        renderFeedbackColumn(
          `feedback.${feedbackKey}`,
          columnIndex,
          columnOverrides.find(
            (col) => col.column === `feedback.${feedbackKey}`
          )
        ),
      renderMetricsColumn: (
        metric: ExperimentViewMetric,
        columnIndex: number
      ) => renderMetricsColumn(`metrics.${metric.name}`, columnIndex),
      renderComputedColumn,
    };

    overriddenColumns = renderOverriddenColumns(
      columnOverrides,
      {
        isReferenceInputHidden,
        isAttachmentsHidden,
        isReferenceOutputHidden,
        hiddenFeedbackColumns,
        hiddenMetricsColumns,
      },
      renderFunctions,
      aggregateFeedbackKeys,
      showFeedbackAndMetricsColumns
    );
  }

  const displayedMetricNames = DEFAULT_METRIC_NAMES.filter(
    (metric) => !hiddenMetricsColumns.includes(metric)
  );

  const defaultDisplayFbKeys = Array.from(
    new Set([...aggregateFeedbackKeys, ...(evaluatorFeedbackKeys ?? [])])
  );

  return (
    <Fragment key={`table-row-${example.id}`}>
      {columnOverrides ? (
        overriddenColumns
      ) : (
        // Default rendering order when no columnOverride
        <>
          {renderInputsColumn('inputs')}
          {renderAttachmentsColumn()}
          {renderReferenceOutputsColumn('reference_outputs')}
          {columnsSorted.map((col, sessionIndex) => {
            const sessionId: string | undefined =
              col['sessionId'] ?? col['columnId'];
            const width =
              columnWidthStorage[sessionIndex + OUTPUT_START_COLUMN_IDX] ??
              DEFAULT_COLUMN_WIDTHS;
            return (
              <DatasetSessionCompareRunCell
                key={`run-cell-${sessionId}-${example.id}`}
                hiddenFeedbackKeys={displaySettingsState.hiddenFeedbackColumns}
                hiddenMetrics={displaySettingsState.hiddenMetricsColumns}
                experimentInProgress={experimentInProgress}
                expectedFeedbackKeys={
                  example.runs.every((r) => r.error)
                    ? []
                    : evaluatorFeedbackKeys
                }
                expanded={feedbackChipsExpanded}
                setExpanded={setFeedbackChipsExpanded}
                openDetailsOrRepetitionsPane={openDetailsOrRepetitionsPane}
                example={example}
                column={col}
                sessionIds={sessionIds}
                isPairwiseUIEnabled={isPairwiseUIEnabled}
                isRegressionTrackingUIEnabled={isRegressionTrackingUIEnabled}
                selectedBaselineSessionId={selectedBaselineSessionId}
                regressionsFilterFeedbackKey={regressionsFilterFeedbackKey}
                isTextTruncated={isTextTruncated}
                getRunDiffs={getRunDiffs}
                diffs={
                  textDisplayMode === TextDisplayMode.DIFF
                    ? storedDiffs[example.id]?.runs
                    : undefined
                }
                language={language}
                onOpenDetailSidebar={toggleRowExampleDetail}
                onOpenTraceDetailSidebar={toggleRunDetail}
                backgroundStyles={backgroundStyles}
                hideFeedback={hideFeedback}
                hideMetrics={hideMetrics}
                columnIndex={sessionIndex + OUTPUT_START_COLUMN_IDX}
                dataType={
                  dataType ??
                  (example.runs.every((r) => isMessageLike(r.outputs?.output))
                    ? 'chat'
                    : 'kv')
                }
                mutateInfiniteExamples={mutateInfiniteExamples}
                comparativeExperiment={comparativeExperiment ?? undefined}
                datasetShareToken={datasetShareToken}
                style={{
                  width,
                  maxWidth: width,
                  minWidth: width,
                }}
                onViewEvaluatorRun={toggleRunDetail}
                sessions={sessionsData}
              />
            );
          })}

          {showFeedbackAndMetricsColumns ? (
            <>
              {defaultDisplayFbKeys.map((feedbackKey, idx) =>
                renderFeedbackColumn(
                  `feedback.${feedbackKey}`,
                  OUTPUT_START_COLUMN_IDX + 1 + idx
                )
              )}
              {displayedMetricNames.map((metricName, i) =>
                renderMetricsColumn(
                  `metrics.${metricName}`,
                  OUTPUT_START_COLUMN_IDX +
                    1 +
                    aggregateFeedbackKeys.filter(
                      (key) => !hiddenFeedbackColumns.includes(key)
                    ).length +
                    i
                )
              )}
            </>
          ) : null}
        </>
      )}
      {customExtraTableCell?.(backgroundStyles)}

      {onDelete && (
        <MoreActionsButton
          isLoading={isDeleteLoading ?? false}
          onDelete={onDelete}
        />
      )}
      <td
        className={cn(
          'group h-full overflow-hidden border-b border-r border-secondary',
          backgroundStyles
        )}
      />
    </Fragment>
  );
}
